import { useMemo } from 'react';
import { processHeading, HeadingType } from '@workspace/lib/heading-utils';
import { getBackgroundClass, shouldOpenInNewTab } from '@workspace/lib';
import { stripInvisibles } from '@workspace/constants';
import { ProductTypeBlock, DisplayProduct, ProductTypeButton } from './product-type.types';

const DEFAULT_HEADING: HeadingType = {
  _type: 'styledHeading',
  fontSize: 'h2',
  headingText: 'Product Types',
} as const;

export function useProductType(block: ProductTypeBlock) {
  const heading = useMemo(() => block?.heading || DEFAULT_HEADING, [block?.heading]);

  const resolvedTitleText = useMemo(() => {
    const { text } = processHeading(heading);
    return text;
  }, [heading]);

  const buttons = useMemo(() => (Array.isArray(block?.buttons) ? block.buttons : []), [block?.buttons]);

  const alignmentClass = useMemo(() => {
    const rawAlignment = stripInvisibles((block?.headingAlignment || 'left').toString())
      .trim()
      .toLowerCase();
    if (rawAlignment === 'middle') {
      return 'text-center w-full';
    }
    if (rawAlignment === 'right') {
      return 'text-right w-full';
    }
    return 'text-left w-full';
  }, [block?.headingAlignment]);

  const displayProducts = useMemo(() => {
    const productCards = Array.isArray(block?.productCards) ? block.productCards : [];
    return productCards
      .map((card) => (card && card.productDetail ? { ...card.productDetail, link: card.link } : null))
      .filter(Boolean) as DisplayProduct[];
  }, [block?.productCards]);

  const backgroundClass = useMemo(() => getBackgroundClass(block.backgroundColor), [block.backgroundColor]);

  const getButtonUrl = (button: ProductTypeButton): string => {
    if (button.link?.type === 'anchor') {
      return button.link.anchor ? `#${button.link.anchor}` : '#';
    }
    return button.link?.url || '#';
  };

  const getOpenInNewTab = (button: ProductTypeButton): boolean => {
    return shouldOpenInNewTab(button.link);
  };

  return {
    resolvedTitleText,
    buttons,
    alignmentClass,
    displayProducts,
    backgroundClass,
    getButtonUrl,
    getOpenInNewTab,
  };
}
